function countUniqueBlockIds() {
  return new Set([...document.querySelectorAll('block-loader[block-id]')].map(block => block.getAttribute('block-id'))).size;
}

class BlockStore {
  static store = {};
  static loadingPromises = {};
  static updatedBlocks = new Set(); // Track updated blocks

  static getStoreKey(handle, sectionId) {
    return `${handle}:${sectionId}`;
  }

  static initializeStore(handle, sectionId) {
    const storeKey = this.getStoreKey(handle, sectionId);
    if (!this.store[storeKey]) {
      this.store[storeKey] = {};
    }
    return storeKey;
  }

  static loadBlocks(handle, sectionId) {
    const storeKey = this.getStoreKey(handle, sectionId);

    if (this.loadingPromises[storeKey]) {
      return this.loadingPromises[storeKey];
    }

    this.initializeStore(handle, sectionId);

    if (Object.keys(this.store[storeKey]).length === countUniqueBlockIds()) {
      return Promise.resolve(this.store[storeKey]);
    }

    this.loadingPromises[storeKey] = fetch(
      `${window.Shopify.routes.root}products/${handle}?section_id=${sectionId}`
    )
      .then(response => {
        if (!response.ok) throw new Error(response.statusText);
        return response.text();
      })
      .then(result => {
        const html = new DOMParser().parseFromString(result, 'text/html');
        const blocks = html.querySelectorAll('block-loader[block-id]');

        blocks.forEach(block => {
          const blockId = block.getAttribute('block-id');
          if (blockId) {
            this.store[storeKey][blockId] = block.innerHTML;
          }
        });

        delete this.loadingPromises[storeKey];
        return this.store[storeKey];
      })
      .catch(error => {
        console.error(`Error fetching blocks for product handle ${handle}, section ${sectionId}:`, error);
        delete this.loadingPromises[storeKey];
        throw error;
      });

    return this.loadingPromises[storeKey];
  }

  static getBlockContent(handle, sectionId, blockId) {
    const storeKey = this.getStoreKey(handle, sectionId);
    return this.store[storeKey]?.[blockId];
  }

  static setBlockContent(handle, sectionId, blockId, content) {
    const storeKey = this.initializeStore(handle, sectionId);
    this.store[storeKey][blockId] = content;
  }

  static resetUpdatedBlocks() {
    this.updatedBlocks.clear();
  }

  static checkAllBlocksUpdated() {
    const totalBlocks = countUniqueBlockIds();
    if (this.updatedBlocks.size === totalBlocks) {
      window.dispatchEvent(new CustomEvent('blocks:updated', {
        detail: { totalBlocks }
      }));
    }
  }
}

window.BlockStore = BlockStore;

class BlockLoader extends HTMLElement {
  constructor() {
    super();
    this.blockId = this.getAttribute('block-id');
    this.section = this.closest('.shopify-section');
    this.sectionId = this.section?.id.replace('shopify-section-', '');

    // Drawer teleportation clones markup outside `.shopify-section`.
    // In that cloned context block-loader should be inert.
    if (!this.sectionId && this.closest('slide-drawer')) {
      return;
    }

    if (!this.blockId || !this.sectionId) {
      console.error('BlockLoader: Missing required attributes', {
        blockId: this.blockId,
        sectionId: this.sectionId
      });
      return;
    }

    const handle = window.location.pathname.split('/')[2];
    this.storeContent(handle);

    window.addEventListener('product:change', this.handleProductChange.bind(this));
  }

  storeContent(handle) {
    if (!handle || !this.blockId) return;
    BlockStore.setBlockContent(handle, this.sectionId, this.blockId, this.innerHTML);
  }

  async handleProductChange(event) {
    const handle = event.detail.product?.handle;
    if (!handle) {
      console.error('BlockLoader: Missing product handle in event', event);
      return;
    }

    // Reset updated blocks tracking on product change
    BlockStore.resetUpdatedBlocks();

    try {
      await BlockStore.loadBlocks(handle, this.sectionId);
      this.updateContent(handle);
    } catch (error) {
      console.error('Failed to update block content:', error);
    }
  }

  updateContent(handle) {
    const content = BlockStore.getBlockContent(handle, this.sectionId, this.blockId);

    if (content === undefined) {
      console.warn(`BlockLoader: No content found for block ${this.blockId} in section ${this.sectionId}`);
      return;
    }

    this.innerHTML = content;
    BlockStore.updatedBlocks.add(this.blockId);
    BlockStore.checkAllBlocksUpdated();
  }
}

customElements.define('block-loader', BlockLoader);
